"use client";

import { useEffect, useRef } from "react";

export type ScrollRevealMountProps = {
    /** ID du nœud déjà présent dans le DOM (rendu côté serveur) */
    targetId: string;
    /** fade = le conteneur ; stagger = chaque enfant direct */
    mode: "fade" | "stagger";
    duration?: number;
    delay?: number;
    staggerEach?: number;
    start?: string;
};

/**
 * Ne rend rien (`null`) : zéro différence SSR / client → pas d’erreur d’hydratation.
 * Les classes / data-* / styles initiaux doivent être sur l’élément `#targetId`
 * rendu par un Server Component (voir globals.css `.scroll-reveal-prehide`, `[data-gsap-stagger="true"]`).
 */
export default function ScrollRevealMount({
    targetId,
    mode,
    duration = 0.65,
    delay = 0,
    staggerEach = 0.09,
    start = "top 88%",
}: ScrollRevealMountProps) {
    const didInitRef = useRef(false);

    useEffect(() => {
        const el = document.getElementById(targetId);
        if (!el) return;
        if (didInitRef.current) return;

        didInitRef.current = true;

        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reducedMotion) {
            if (mode === "stagger") {
                const targets = Array.from(el.children) as HTMLElement[];
                targets.forEach((child) => {
                    child.style.opacity = "1";
                    child.style.transform = "translateY(0px)";
                });
            } else {
                const node = el as HTMLElement;
                node.style.opacity = "1";
                node.style.transform = "translateY(0px)";
            }
            return;
        }

        let didStart = false;
        const init = async () => {
            if (didStart) return;
            didStart = true;

            const { gsap } = await import("gsap");
            const { ScrollTrigger } = await import("gsap/ScrollTrigger");
            gsap.registerPlugin(ScrollTrigger);

            if (mode === "stagger") {
                const targets = gsap.utils.toArray<HTMLElement>(el.children);
                if (!targets.length) return;

                gsap.to(targets, {
                    opacity: 1,
                    y: 0,
                    duration,
                    delay,
                    stagger: { each: staggerEach },
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: el,
                        start,
                        toggleActions: "play none none none",
                    },
                });
            } else {
                gsap.to(el, {
                    opacity: 1,
                    y: 0,
                    duration,
                    delay,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: el,
                        start,
                        toggleActions: "play none none none",
                    },
                });
            }
        };

        const io = new IntersectionObserver(
            ([entry]) => {
                if (!entry?.isIntersecting) return;
                io.disconnect();
                void init();
            },
            { rootMargin: "0px 0px", threshold: 0.01 },
        );

        io.observe(el);

        // Si déjà visible (cas mobile), on initialise tout de suite.
        const rect = el.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
            io.disconnect();
            void init();
        }

        return () => {
            io.disconnect();
        };
    }, [targetId, mode, duration, delay, staggerEach, start]);

    return null;
}
